import { useMemo } from 'react'
import { useTokenAcrossChains } from '@/src/queries/hooks/tokens'
import type { UseTokenFieldActionsProps } from '@/src/stores/swap/actions'
import { useNetworkSelection } from '@/src/stores/token-selection/actions'
import { ChainImg } from '../ui/chain-img'
import { Skeleton } from '../ui/skeleton'

interface SelectTokenNetworkProps {
  chain?: string
  token?: string
  direction?: UseTokenFieldActionsProps['direction']
}

/** Button and dialog that allows a user to select the network for a given token. */
export function SelectTokenNetwork({
  chain,
  token: tokenProp,
  direction,
}: SelectTokenNetworkProps) {
  const { open: openNetworkSelection } = useNetworkSelection()

  const {
    data: tokenAcrossChains,
    isLoading: isTokenAcrossChainsLoading,
    error: tokenAcrossChainsError,
  } = useTokenAcrossChains(chain, tokenProp)
  const token = tokenAcrossChains?.requested_asset

  const isLoading = isTokenAcrossChainsLoading

  const isNetworkNotFoundError = useMemo(() => {
    const cause = tokenAcrossChainsError?.cause

    if (!cause || typeof cause !== 'string') return false

    return cause.includes('chain not found')
  }, [tokenAcrossChainsError?.cause])

  if (isNetworkNotFoundError) {
    return <span className='text-sm text-secondary'>Unknown network</span>
  }

  return (
    <>
      {isLoading && !token ? (
        <Skeleton className='w-32 h-6' />
      ) : (
        token && (
          <button
            type='button'
            onClick={() =>
              openNetworkSelection({
                chainId: token.chain_id,
                denom: token.denom,
                ticker: token.ticker,
                tokenImg: token.logo_img_url,
                displayName: token.kind_display_name,
                direction,
              })
            }
            className='flex items-center gap-1 rounded-xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-focus focus-visible:outline-offset-4 transition-colors group'
          >
            <span className='text-sm text-secondary'>on</span>
            <div className='flex items-center gap-2 pl-1'>
              {token?.chain_img && (
                <ChainImg
                  chainImg={token.chain_img}
                  chainName={token.chain_name}
                  chainId={token.chain_id}
                />
              )}
              <span className='text-sm text-link group-hover:text-link-hover'>
                {token?.chain_name}
              </span>
            </div>
          </button>
        )
      )}
    </>
  )
}
